import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/lib/auth";
import { Toaster } from "@/components/ui/sonner";

import Landing from "@/pages/Landing";
import Auth from "@/pages/Auth";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Onboarding from "@/pages/Onboarding";
import AppShell from "@/components/AppShell";
import Dashboard from "@/pages/Dashboard";
import BrandProfile from "@/pages/BrandProfile";
import WeeklyPlan from "@/pages/WeeklyPlan";
import FilmThis from "@/pages/FilmThis";
import ScriptVariantsPage from "@/pages/ScriptVariantsPage";
import Performance from "@/pages/Performance";
import Review from "@/pages/Review";
import BeliefJourney from "@/pages/BeliefJourney";
import Repurpose from "@/pages/Repurpose";
import Connections from "@/pages/Connections";
import Billing from "@/pages/Billing";
import Pricing from "@/pages/Pricing";
import CreateProfile from "@/pages/CreateProfile";
import Admin from "@/pages/Admin";
import Studio from "@/pages/Studio";
import StudioProject from "@/pages/StudioProject";
import Terms from "@/pages/Terms";
import Privacy from "@/pages/Privacy";
import Refunds from "@/pages/Refunds";

function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  return children;
}

function RequireOnboarded({ children }) {
  const { user, loading } = useAuth();
  if (loading) return <div className="p-10 text-sm text-muted-foreground">Loading…</div>;
  if (!user) return <Navigate to="/auth" replace />;
  if (!user.onboarded) return <Navigate to="/onboarding" replace />;
  return children;
}

export default function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/create-profile" element={<CreateProfile />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/refunds" element={<Refunds />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/auth/forgot" element={<ForgotPassword />} />
          <Route path="/auth/reset" element={<ResetPassword />} />
          <Route path="/onboarding" element={<RequireAuth><Onboarding /></RequireAuth>} />
          <Route element={<RequireOnboarded><AppShell /></RequireOnboarded>}>
            <Route path="/app" element={<Dashboard />} />
            <Route path="/app/brand" element={<BrandProfile />} />
            <Route path="/app/plan" element={<WeeklyPlan />} />
            <Route path="/app/film" element={<FilmThis />} />
            <Route path="/app/variants/:postId" element={<ScriptVariantsPage />} />
            <Route path="/app/repurpose/:postId" element={<Repurpose />} />
            <Route path="/app/performance" element={<Performance />} />
            <Route path="/app/review" element={<Review />} />
            <Route path="/app/belief" element={<BeliefJourney />} />
            <Route path="/app/connections" element={<Connections />} />
            <Route path="/app/billing" element={<Billing />} />
            <Route path="/app/studio" element={<Studio />} />
            <Route path="/app/studio/:projectId" element={<StudioProject />} />
            <Route path="/app/admin" element={<Admin />} />
          </Route>
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors position="top-right" />
    </AuthProvider>
  );
}
